import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, X, Menu, User, LogOut, Settings, 
  Heart, Clock, Home, ChevronDown, Star, PlusSquare,
  Sparkles, Shield, TrendingUp, Zap
} from 'lucide-react';

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef(null);
  const profileRef = useRef(null);
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const lastScrollY = useRef(0);

  const canViewProperties = user?.tipo_usuario_id && [2, 3, 4].includes(user.tipo_usuario_id);
  const isAdminUser = user?.tipo_usuario_id === 5;

  // Memoized stable particles to prevent re-renders
  const particles = useMemo(() => {
    return Array.from({ length: 8 }, (_, i) => ({
      id: i,
      size: Math.random() * 120 + 60,
      x: Math.random() * 100,
      y: Math.random() * 100,
      duration: Math.random() * 25 + 20,
      delay: Math.random() * 8,
      opacity: Math.random() * 0.08 + 0.02
    }));
  }, []); // Empty dependency array ensures this only runs once

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    setIsProfileOpen(false);
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/buscar?tituloSearch=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  // Enhanced scroll handler
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > 100) {
        setShowMobileNav(true);
      } else {
        setShowMobileNav(false);
      }
      
      if (currentScrollY < 10) {
        setVisible(true);
      } else if (currentScrollY < lastScrollY.current) {
        setVisible(true);
      } else if (currentScrollY > lastScrollY.current) {
        setVisible(false);
      }
      
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    setIsProfileOpen(false);
  }, [location.pathname]);

  // Enhanced animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      } 
    },
    exit: { 
      opacity: 0,
      transition: { 
        duration: 0.3,
        ease: [0.4, 0, 1, 1]
      } 
    }
  };

  const slideFromRight = {
    hidden: { x: 20, opacity: 0, scale: 0.95 },
    visible: { 
      x: 0,
      opacity: 1,
      scale: 1,
      transition: { 
        type: "spring", 
        damping: 20,
        stiffness: 300,
        duration: 0.4
      } 
    },
    exit: { 
      x: 20,
      opacity: 0,
      scale: 0.95,
      transition: { 
        duration: 0.2,
        ease: [0.4, 0, 1, 1]
      } 
    }
  };

  const slideUp = {
    hidden: { y: 100, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: "spring", 
        damping: 25,
        stiffness: 400,
        duration: 0.5
      } 
    },
    exit: { 
      y: 100, 
      opacity: 0,
      transition: { 
        duration: 0.3,
        ease: [0.4, 0, 1, 1]
      } 
    }
  };

  const headerVariants = {
    visible: { 
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 30,
        duration: 0.6
      }
    },
    hidden: { 
      y: -100,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 40,
        duration: 0.4
      }
    }
  };

  // Enhanced Button Component
  const HeaderButton = ({ children, variant = 'default', className = '', ...props }) => {
    const baseClasses = "relative overflow-hidden transition-all duration-300 ease-out transform";
    
    const variants = {
      default: "text-white/80 hover:text-white hover:bg-white/10 rounded-xl px-4 py-2.5",
      primary: "bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 hover:from-blue-400 hover:via-blue-500 hover:to-indigo-500 text-white rounded-xl px-6 py-2.5 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40",
      icon: "text-white/80 hover:text-white hover:bg-white/10 rounded-xl p-2.5"
    };

    return (
      <motion.button
        className={`${baseClasses} ${variants[variant]} ${className}`}
        whileHover={{ scale: 1.02, y: -1 }}
        whileTap={{ scale: 0.98 }}
        {...props}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 
                        translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
        <span className="relative z-10 flex items-center gap-2">
          {children}
        </span>
      </motion.button>
    );
  };

  return (
    <>
      <motion.header 
        className="sticky top-0 z-50"
        initial="visible"
        animate={visible ? "visible" : "hidden"}
        variants={headerVariants}
      >
        <div className="relative">
          {/* Enhanced Background with glassmorphism */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-blue-900/95 to-indigo-900/95 overflow-hidden">
            {/* Stable Animated Particles */}
            {particles.map((particle) => (
              <motion.div
                key={particle.id}
                className="absolute rounded-full bg-gradient-to-br from-blue-400/10 to-purple-400/10 blur-2xl"
                style={{
                  width: `${particle.size}px`,
                  height: `${particle.size}px`,
                  left: `${particle.x}%`,
                  top: `${particle.y}%`,
                  opacity: particle.opacity,
                }}
                animate={{
                  x: [0, 30, -20, 0],
                  y: [0, -25, 20, 0],
                  scale: [1, 1.2, 0.8, 1],
                }}
                transition={{
                  duration: particle.duration,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: particle.delay,
                }}
              />
            ))}
            
            {/* Enhanced Mesh Grid Overlay */}
            <div 
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '24px 24px'
              }}
            />

            {/* Gradient Overlay for depth */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/5" />
          </div>

          {/* Enhanced Header content with backdrop blur */}
          <div className="relative backdrop-blur-md bg-black/20 border-b border-white/20">
            {/* Blue tint overlay for better text readability */}
            <div className="absolute inset-0 bg-blue-900/30"></div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              <div className="flex items-center justify-between h-16">
                {/* Enhanced Logo */}
                <motion.div 
                  className="flex-shrink-0"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                >
                  <Link 
                    to="/" 
                    className="group flex items-center gap-3 hover:scale-105 transition-transform duration-300"
                  >
                    <div className="relative">
                      <img 
                        src="/InKairos-white.ico" 
                        alt="InKayros Logo" 
                        className="w-10 h-10 drop-shadow-lg" 
                      />
                      <div className="absolute -inset-1 bg-gradient-to-r from-blue-400/30 to-purple-400/30 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    {/* Desktop logo text */}
                    <div className="hidden md:block">
                      <div className="flex items-baseline gap-1">
                        <span className="text-white text-xl font-bold tracking-tight">
                          InKayros
                        </span>
                        <span className="text-blue-300 text-sm font-medium">
                          .com
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>

                {/* Enhanced Desktop Navigation */}
                <div className="hidden md:flex items-center space-x-2">
                  {/* Enhanced Search Button */}
                  <HeaderButton
                    variant="icon"
                    onClick={() => setIsSearchOpen(true)}
                    className="group"
                  >
                    <Search className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
                    <Sparkles className="w-3 h-3 absolute -top-0.5 -right-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </HeaderButton>

                  {/* Enhanced Favorites */}
                  <Link to="/favoritos">
                    <HeaderButton variant="default" className="group">
                      <Heart className="w-4 h-4 transition-all duration-300 group-hover:text-red-400 group-hover:scale-110" />
                      <span className="font-medium">Favoritos</span>
                    </HeaderButton>
                  </Link>

                  {/* Enhanced Publish Button */}
                  <Link to="/publicar">
                    <HeaderButton variant="primary" className="group font-semibold">
                      <TrendingUp className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
                      <span>Publicar</span>
                      <Zap className="w-3 h-3 opacity-70" />
                    </HeaderButton>
                  </Link>

                  {/* Enhanced User Profile or Login */}
                  {user ? (
                    <div ref={profileRef} className="relative">
                      <HeaderButton
                        variant="default"
                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                        className="group"
                      >
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-medium max-w-24 truncate">
                          {user.nombre || 'Usuario'}
                        </span>
                        <ChevronDown className={`w-4 h-4 transition-all duration-300
                                            ${isProfileOpen ? 'rotate-180 text-blue-300' : 'group-hover:text-blue-300'}`} />
                        {(user.email_verificado && user.telefono_verificado) && (
                          <Shield className="w-3 h-3 text-green-400 absolute -top-1 -right-1" />
                        )}
                      </HeaderButton>

                      <AnimatePresence>
                        {isProfileOpen && (
                          <motion.div
                            className="absolute right-0 mt-3 w-64 py-3 bg-white/95 backdrop-blur-xl rounded-2xl
                                   border border-white/20 shadow-2xl overflow-hidden z-10"
                            variants={slideFromRight}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                          >
                            {/* Profile Header */}
                            <div className="px-4 pb-3 border-b border-gray-100">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                                  <User className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-900">{user.nombre || 'Usuario'}</div>
                                  <div className="text-sm text-gray-500">{user.email}</div>
                                </div>
                              </div>
                            </div>

                            {/* Menu Items */}
                            <div className="py-2">
                              {isAdminUser ? (
                                <Link
                                  to="/admin/dashboard"
                                  className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200"
                                >
                                  <Star className="w-5 h-5 mr-3 text-yellow-500" />
                                  <span className="font-medium">Dashboard</span>
                                </Link>
                              ) : (
                                <>
                                  <Link
                                    to="/mi_perfil"
                                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200"
                                  >
                                    <User className="w-5 h-5 mr-3 text-blue-500" />
                                    <span className="font-medium">Mi Perfil</span>
                                  </Link>
                                  {canViewProperties && (
                                    <Link
                                      to="/mis_propiedades"
                                      className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200"
                                    >
                                      <Home className="w-5 h-5 mr-3 text-indigo-500" />
                                      <span className="font-medium">Mis Propiedades</span>
                                    </Link>
                                  )}
                                  <Link
                                    to="/historial"
                                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200"
                                  >
                                    <Clock className="w-5 h-5 mr-3 text-purple-500" />
                                    <span className="font-medium">Historial</span>
                                  </Link>
                                  <Link
                                    to="/configuracion"
                                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200"
                                  >
                                    <Settings className="w-5 h-5 mr-3 text-gray-500" />
                                    <span className="font-medium">Configuración</span>
                                  </Link>
                                </>
                              )}
                              
                              {/* Divider */}
                              <div className="my-2 border-t border-gray-100" />
                              
                              <button
                                onClick={handleLogout}
                                className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-all duration-200"
                              >
                                <LogOut className="w-5 h-5 mr-3" />
                                <span className="font-medium">Cerrar Sesión</span>
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link to="/login">
                      <HeaderButton variant="default" className="group">
                        <User className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
                        <span className="font-medium">Ingresar</span>
                      </HeaderButton>
                    </Link>
                  )}
                </div>

                {/* Enhanced Mobile Menu Button */}
                <HeaderButton
                  variant="icon"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="md:hidden"
                >
                  <motion.div
                    animate={{ rotate: isMenuOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                  </motion.div>
                </HeaderButton>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Full-screen Search */}
        <AnimatePresence>
          {isSearchOpen && (
            <motion.div 
              className="fixed inset-0 bg-slate-900/96 backdrop-blur-xl z-50 flex items-start pt-24 justify-center px-4"
              variants={fadeIn}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {/* Blue tint overlay for better text readability */}
              <div className="absolute inset-0 bg-blue-900/40"></div>
              <div 
                ref={searchRef}
                className="w-full max-w-3xl relative z-10"
              >
                <div className="flex justify-between items-center mb-8">
                  <div>
                    <h2 className="text-3xl font-bold text-white mb-2">Buscar Propiedades</h2>
                    <p className="text-white/70">Encuentra tu hogar ideal</p>
                  </div>
                  <button 
                    onClick={() => setIsSearchOpen(false)}
                    className="p-3 text-white/80 hover:text-white rounded-2xl hover:bg-white/10 transition-all duration-300"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <form onSubmit={handleSearch}>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Buscar casas, departamentos, terrenos..."
                      className="w-full px-6 py-5 bg-white/10 border border-white/20 text-white text-lg
                             rounded-2xl backdrop-blur-sm transition-all duration-300 
                             focus:bg-white/15 focus:outline-none focus:ring-4 focus:ring-blue-500/30 
                             focus:border-blue-400/50 placeholder-white/50"
                      autoFocus
                    />
                    <button 
                      type="submit"
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-3 text-white/80 
                             hover:text-white rounded-xl hover:bg-white/10 transition-all duration-300"
                    >
                      <Search className="w-6 h-6" />
                    </button>
                  </div>
                </form>

                <div className="mt-10">
                  <h3 className="text-lg font-semibold text-white/90 mb-4">Búsquedas populares</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      'Casas en venta', 
                      'Departamentos amoblados', 
                      'Terrenos urbanos', 
                      'Locales comerciales'
                    ].map((term) => (
                      <motion.button
                        key={term}
                        onClick={() => setSearchQuery(term)}
                        className="px-4 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-sm text-white/80 hover:text-white 
                               transition-all duration-300 backdrop-blur-sm border border-white/10 hover:border-white/20"
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {term}
                      </motion.button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Enhanced Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div 
              className="md:hidden fixed inset-0 pt-15 bg-slate-900/96 backdrop-blur-xl z-40 overflow-y-auto"
              variants={fadeIn}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {/* Blue tint overlay for better text readability */}
              <div className="absolute inset-0 bg-blue-900/40"></div>
              <div className="px-6 py-8 space-y-6 relative z-10">
                {/* Enhanced Header */}
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white">Menú</h2>
                    <p className="text-white/70 text-sm">Navegación principal</p>
                  </div>
                  <button 
                    onClick={() => setIsMenuOpen(false)}
                    className="p-2 text-white/80 hover:text-white rounded-xl hover:bg-white/10 transition-all duration-300"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Enhanced Search Bar */}
                <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                  <form onSubmit={handleSearch} className="w-full">
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar propiedades..."
                        className="w-full px-12 py-4 bg-white/10 border border-white/20 text-white
                             rounded-xl backdrop-blur-sm transition-all duration-300 
                             focus:bg-white/15 focus:outline-none focus:ring-2 focus:ring-blue-400/50 placeholder-white/50"
                      />
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 w-5 h-5" />
                      <button 
                        type="submit"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white rounded-lg hover:bg-white/10"
                      >
                        <Search className="w-4 h-4" />
                      </button>
                    </div>
                  </form>
                </div>

                {/* Enhanced User Profile Section */}
                {user ? (
                  <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                    <div className="flex items-center justify-between mb-4">
                      <Link
                        to="/mi_perfil"
                        className="flex items-center space-x-4 text-white"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl flex items-center justify-center">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <div className="font-semibold text-lg">{user.nombre || 'Usuario'}</div>
                          <div className="text-white/70 text-sm">{user.email}</div>
                        </div>
                      </Link>

                      <button
                        onClick={() => setIsSubMenuOpen(!isSubMenuOpen)}
                        className="text-white/80 hover:text-white focus:outline-none p-2 rounded-xl hover:bg-white/10"
                      >
                        <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isSubMenuOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>

                    <AnimatePresence>
                      {isSubMenuOpen && (
                        <motion.div 
                          className="space-y-2"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {isAdminUser ? (
                            <Link
                              to="/admin/dashboard"
                              className="flex items-center px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-200"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <Star className="w-5 h-5 mr-3" />
                              <span className="font-medium">Dashboard</span>
                            </Link>
                          ) : (
                            <>
                              {canViewProperties && (
                                <Link
                                  to="/mis_propiedades"
                                  className="flex items-center px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-200"
                                  onClick={() => setIsMenuOpen(false)}
                                >
                                  <Home className="w-5 h-5 mr-3" />
                                  <span className="font-medium">Mis Propiedades</span>
                                </Link>
                              )}
                              <Link
                                to="/historial"
                                className="flex items-center px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-200"
                                onClick={() => setIsMenuOpen(false)}
                              >
                                <Clock className="w-5 h-5 mr-3" />
                                <span className="font-medium">Historial</span>
                              </Link>
                              <Link
                                to="/configuracion"
                                className="flex items-center px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-200"
                                onClick={() => setIsMenuOpen(false)}
                              >
                                <Settings className="w-5 h-5 mr-3" />
                                <span className="font-medium">Configuración</span>
                              </Link>
                            </>
                          )}
                          <button
                            onClick={handleLogout}
                            className="flex items-center w-full px-4 py-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl transition-all duration-200"
                          >
                            <LogOut className="w-5 h-5 mr-3" />
                            <span className="font-medium">Cerrar Sesión</span>
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    className="flex items-center px-5 py-4 text-white bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-200 border border-white/10"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User className="w-6 h-6 mr-4" />
                    <span className="font-medium text-lg">Ingresar</span>
                  </Link>
                )}

                {/* Enhanced Main Menu */}
                <div className="space-y-3">
                  <Link
                    to="/favoritos"
                    className="flex items-center px-5 py-4 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Heart className="w-6 h-6 mr-4" />
                    <span className="font-medium text-lg">Favoritos</span>
                  </Link>

                  <Link
                    to="/publicar"
                    className="flex items-center px-5 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 
                         text-white rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl hover:from-blue-500 hover:to-indigo-500"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <TrendingUp className="w-6 h-6 mr-4" />
                    <span className="font-semibold text-lg">Publicar</span>
                    <Sparkles className="w-4 h-4 ml-auto" />
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Enhanced Mobile Bottom Navigation */}
      <AnimatePresence>
        {showMobileNav && (
          <motion.div 
            className="md:hidden fixed bottom-0 left-0 right-0 z-50 pb-safe"
            variants={slideUp}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="bg-slate-900/95 backdrop-blur-xl border-t border-white/20 shadow-2xl">
              <div className="grid grid-cols-5 h-20 px-2">
                {/* Home */}
                <Link 
                  to="/" 
                  className="flex flex-col items-center justify-center text-white/70 hover:text-white transition-all duration-300 rounded-xl hover:bg-white/5"
                >
                  <Home className="w-6 h-6 mb-1" />
                  <span className="text-xs font-medium">Inicio</span>
                </Link>
                
                {/* Search */}
                <button 
                  onClick={() => setIsSearchOpen(true)}
                  className="flex flex-col items-center justify-center text-white/70 hover:text-white transition-all duration-300 rounded-xl hover:bg-white/5"
                >
                  <Search className="w-6 h-6 mb-1" />
                  <span className="text-xs font-medium">Buscar</span>
                </button>
                
                {/* Publish - Enhanced center button */}
                <div className="flex flex-col items-center justify-center relative">
                  <Link 
                    to="/publicar" 
                    className="absolute -top-6 p-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 shadow-2xl shadow-blue-600/30 hover:shadow-blue-600/50 transition-all duration-300 hover:scale-105 border border-blue-500/20"
                  >
                    <PlusSquare className="w-6 h-6 text-white" />
                  </Link>
                  <span className="text-xs font-medium text-white mt-10">Publicar</span>
                </div>
                
                {/* Favorites */}
                <Link 
                  to="/favoritos" 
                  className="flex flex-col items-center justify-center text-white/70 hover:text-white transition-all duration-300 rounded-xl hover:bg-white/5"
                >
                  <Heart className="w-6 h-6 mb-1" />
                  <span className="text-xs font-medium">Favoritos</span>
                </Link>
                
                {/* Profile */}
                <Link 
                  to={user ? "/mi_perfil" : "/login"} 
                  className="flex flex-col items-center justify-center text-white/70 hover:text-white transition-all duration-300 rounded-xl hover:bg-white/5"
                >
                  <div className="relative">
                    <User className="w-6 h-6 mb-1" />
                    {user && (user.email_verificado && user.telefono_verificado) && (
                      <Shield className="w-3 h-3 text-green-400 absolute -top-1 -right-1" />
                    )}
                  </div>
                  <span className="text-xs font-medium">{user ? "Perfil" : "Ingresar"}</span>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Add padding when mobile nav is showing */}
      {showMobileNav && <div className="md:hidden h-20" />}
    </>
  );
};

export default Header;